import { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { IoClose } from "react-icons/io5";
import toast from "react-simple-toasts";
import PhoneInput from "components/shared/CountryPhone/CountryPhoneDropdown";
import { LuAsterisk } from "react-icons/lu";
import { LoaderX } from "components/shared/Loader";
import HoneypotField from "components/shared/HoneypotField";
import useSpamGuard from "hooks/useSpamGuard";

type data = {
  businessName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  description?: string;
};

let schema = Yup.object({
  businessName: Yup.string()
    .min(2, "Business name cannot be less than two characters")
    .required("Please input Business name"),
  firstName: Yup.string()
    .min(2, "First name cannot be less than two characters")
    .required("Please input First name"),
  lastName: Yup.string()
    .min(2, "Last name cannot be less than two characters")
    .required("Please input Last name"),
  phone: Yup.string()
    .min(9, "Phone cannot be less than nine characters")
    .required("Please input Phone number"),
  email: Yup.string()
    .email("Please enter a valid email")
    .required("Please input Email"),
  description: Yup.string(),
});

const ContactSalesModal = ({ closer }: { closer?: any }) => {
  const [submitting, setSubmitting] = useState(false);
  const { honeypotProps, isLikelyBot } = useSpamGuard();

  const submitForm = async (data: data) => {
    // Drop silently and mirror the success path, so a bot gets no signal.
    if (isLikelyBot()) {
      toast(
        "Thank you for booking a demo with the sales team. We will reach out soon",
        {
          className: "text-white  z-[99999]",
          time: 4000,
        }
      );
      formik.resetForm();
      closer();
      return;
    }

    setSubmitting(true);
    try {
      const {description, ...rest} = data;
      let _data: any = {
        ...rest,
       ...(description ? {description} : {}),
      };

      let resp = await axios.post(
        `${process.env.REACT_APP_IAM_URL}/leads/request`,
        {
          ..._data,
        }
      );

      if (resp.status < 300) {
        toast(
          "Thank you for booking a demo with the sales team. We will reach out soon",
          {
            className: "text-white  z-[99999]",
            time: 4000,
          }
        );
        formik.resetForm();
        closer();
      } else if(resp.status === 400) {
        toast(`${resp.data?.message}`, {
          className: "text-white  z-[99999]",
          time: 4000,
        });
      } else {
        toast('We did not get your booking. Please try again', {
          className: "text-white  z-[99999]",
          time: 4000,
        })
      }
    } catch (error) {
      let errorMessage = "We did not get your booking. Please try again";
      if (
        typeof error === "object" &&
        error !== null &&
        "response" in error &&
        typeof (error as any).response === "object" &&
        (error as any).response !== null &&
        "data" in (error as any).response &&
        typeof (error as any).response.data === "object" &&
        (error as any).response.data !== null &&
        "message" in (error as any).response.data
      ) {
        errorMessage = (error as any).response.data.message;
      }
      toast(`${errorMessage}`, {
        className: "text-white  z-[99999]",
        time: 4000,
      });
    }
    setSubmitting(false);
  };
  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      businessName: "",
      phone: "",
      description: "",
      
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      await submitForm(values);
    },
  });
  return (
    <div className="m-auto font-satoshi">
      <HoneypotField {...honeypotProps} />

      <div className="flex justify-between items-center mb-5">
        <p className="text-2xl text-center text-blueShades-4 font-semibold">
          Schedule a Demo with Us
        </p>
        <span
          onClick={() => closer()}
          className="rounded-full hover:shadow-lg cursor-pointer  "
        >
          <IoClose size={24} color="black" />
        </span>
      </div>

      <label
        className="text-blueShades-4 mb-6 mt-2 flex flex-col font-medium"
        htmlFor="businessName"
      >
        <p className="flex">
          <span>Business Name</span>
          <span>
            <LuAsterisk className="text-textRed text-sm" />
          </span>
        </p>

        <input
          className="w-full py-3 px-4 font-Regular mt-2 h-9 outline-blueShades-4 border-bbg border  rounded-md bg-white text-bblack-0 placeholder:text-ashShade-16"
          type="text"
          name="businessName"
          placeholder="e.g Microsoft"
          value={formik.values.businessName}
          onChange={formik.handleChange}
        />
        {formik.errors.businessName && formik.touched.businessName && (
          <p className="text-red text-sm text-red-500">
            {formik.errors.businessName}
          </p>
        )}
      </label>
      <div className="w-full flex flex-col md:flex-row justify-between gap-y-3 lg:gap-y-0 md:items-center md:justify-center">
        <label
          className="text-blueShades-4 mt-2 mr-3 flex-1 flex flex-col font-medium"
          htmlFor="firstName"
        >
          <p className="flex">
            <span>First Name</span>
            <span>
              <LuAsterisk className="text-textRed text-sm" />
            </span>
          </p>
          <input
            className="w-full py-3 px-4 mt-2 font-Regular h-9 outline-blueShades-4 border-bbg border  rounded-md bg-white text-bblack-0 placeholder:text-ashShade-16"
            type="text"
            name="firstName"
            placeholder="e.g John"
            value={formik.values.firstName}
            onChange={formik.handleChange}
          />
          {formik.errors.firstName && formik.touched.firstName && (
            <p className="text-red text-sm text-red-500">
              {formik.errors.firstName}
            </p>
          )}
        </label>
        <label
          className="text-blueShades-4 font-medium mb-6 md:mb-0 mt-2 flex flex-1 flex-col"
          htmlFor="lastName"
        >
          <p className="flex">
            <span>Last Name</span>
            <span>
              <LuAsterisk className="text-textRed text-sm" />
            </span>
          </p>
          <input
            className="w-full py-3 px-4 mt-2 font-Regular h-9 outline-blueShades-4 border-bbg border  rounded-md bg-white text-bblack-0 placeholder:text-ashShade-16"
            type="text"
            name="lastName"
            placeholder="e.g Doe"
            value={formik.values.lastName}
            onChange={formik.handleChange}
          />
          {formik.errors.lastName && formik.touched.lastName && (
            <p className="text-red text-sm text-red-500">
              {formik.errors.lastName}
            </p>
          )}
        </label>
      </div>
      <label
        className="text-blueShades-4 mb-6 mt-2 flex flex-col font-medium"
        htmlFor="email"
      >
        <p className="flex">
          <span>Email Address</span>
          <span>
            <LuAsterisk className="text-textRed text-sm" />
          </span>
        </p>
        <input
          className="w-full py-3 px-4  mt-2 font-Regular h-9 outline-blueShades-4 border-bbg border  rounded-md bg-white text-bblack-0 placeholder:text-ashShade-16"
          type="text"
          name="email"
          placeholder="e.g johndoe@example.com"
          value={formik.values.email}
          onChange={formik.handleChange}
        />
        {formik.errors.email && formik.touched.email && (
          <p className="text-red text-sm text-red-500">{formik.errors.email}</p>
        )}
      </label>
      <label
        className="text-blueShades-4 font-medium mb-6 mt-2 flex flex-col "
        htmlFor="phone"
      >
        <p className="flex">
          <span>Phone Number</span>
          <span>
            <LuAsterisk className="text-textRed text-sm" />
          </span>
        </p>
        <PhoneInput
          onPhoneChange={(values) =>
            formik.setFieldValue("phone", values.phoneNumber)
          }
        />
        {formik.errors.phone && formik.touched.phone && (
          <p className="text-red text-sm text-red-500">{formik.errors.phone}</p>
        )}
      </label>

      <label
        className="text-blueShades-4 font-medium mb-6 mt-2 flex flex-col "
        htmlFor="description"
      >
        Description
        <textarea className="w-full py-3 px-4  mt-2 font-Regular h-[100px] outline-blueShades-4 border-bbg border  rounded-md bg-white text-bblack-0 placeholder:text-ashShade-16"
          name="description"
          placeholder="Enter description..."
          value={formik.values.description}
          onChange={formik.handleChange}/>

      </label>
      <div className="flex flex-col md:flex-row justify-center md:justify-end items-center gap-5">
        <button
          onClick={closer}
          className="bg-white text-blueShades-4 border border-ashShade-19 py-[10px] px-[18px] mt-6 flex justify-center rounded-lg font-medium"
        >
          Cancel
        </button>
        <button
          onClick={(e) => formik.handleSubmit()}
          className="text-white bg-blueShades-4 border border-ashShade-19 py-[10px] px-[18px] mt-6 flex justify-center rounded-lg font-medium"
        >
          {submitting ? (
            <div className="w-6 h-6 border-4  rounded-full border-t-white animate-spin border-x-slate-50 border-b-transparent  ">
            </div>
          ) : (
            "Submit"
          )}
        </button>
      </div>
    </div>
  );
};
export default ContactSalesModal;
